import type {
  ApprovalsFieldSettingsType,
  ApprovalsFieldStatusSettingsType,
  ApprovalsFieldValueType,
} from './approvals'
import type {
  CommentsFieldSettingsType,
  CommentsFieldStatusSettingsType,
  CommentsFieldValueType,
} from './comments'
import type {
  GeneratedIntFieldSettingsType,
  GeneratedIntFieldStatusSettingsType,
  GeneratedIntFieldValueType,
} from './generatedInt'
import type {
  LongTextFieldSettingsType,
  LongTextFieldStatusSettingsType,
  LongTextFieldValueType,
} from './longText'
import type {
  PayItemsFieldSettingsType,
  PayItemsFieldStatusSettingsType,
  PayItemsFieldValueType,
} from './payItems'
import type {
  SelectFieldSettingsType,
  SelectFieldStatusSettingsType,
  SelectFieldValueType,
} from './select'
import type {
  SelectedByFieldSettingsType,
  SelectedByFieldStatusSettingsType,
  SelectedByFieldValueType,
} from './selectedBy'

/**
 * Field identifiers supported by custom workflows.
 *
 * These values must stay aligned with the database's `fields_type_enum`.
 */
export const WORKFLOW_FIELD_TYPES = [
  'long_text',
  'select',
  'approvals',
  'generated_int',
  'selected_by',
  'pay_items',
  'comments',
] as const

/**
 * A persisted custom-workflow field identifier.
 */
export type FieldType = (typeof WORKFLOW_FIELD_TYPES)[number]

/**
 * A union of all persisted custom-workflow field settings.
 */
export type FieldSettingsType<Timestamp = Date> =
  | ApprovalsFieldSettingsType<Timestamp>
  | CommentsFieldSettingsType<Timestamp>
  | GeneratedIntFieldSettingsType
  | LongTextFieldSettingsType
  | PayItemsFieldSettingsType
  | SelectFieldSettingsType<Timestamp>
  | SelectedByFieldSettingsType<Timestamp>

/**
 * Field settings after GraphQL JSON serialization.
 */
export type SerializedFieldSettingsType = FieldSettingsType<string>

/**
 * A union of all possible field status settings.
 *
 * You can also think of these as the "overwritten" input and validation settings.
 */
export type FieldStatusSettingsType<Timestamp = Date> =
  | ApprovalsFieldStatusSettingsType<Timestamp>
  | CommentsFieldStatusSettingsType<Timestamp>
  | GeneratedIntFieldStatusSettingsType
  | LongTextFieldStatusSettingsType
  | PayItemsFieldStatusSettingsType
  | SelectFieldStatusSettingsType<Timestamp>
  | SelectedByFieldStatusSettingsType<Timestamp>

/**
 * A union of all possible field values.
 */
export type FieldValueType<Timestamp = Date> =
  | ApprovalsFieldValueType<Timestamp>
  | CommentsFieldValueType<Timestamp>
  | GeneratedIntFieldValueType
  | LongTextFieldValueType
  | PayItemsFieldValueType
  | SelectFieldValueType<Timestamp>
  | SelectedByFieldValueType<Timestamp>
